/// <reference path="../typings/index.d.ts" />

import 'ace';
import * as angular from 'angular';
import { enableES5, setAutoFreeze } from 'immer';
import 'jquery.scrollto';

import { Injectable, Injector, NgZone } from '@angular/core';
import { UpgradeModule } from '@angular/upgrade/static';

import { NavigationStart, Router } from '@angular/router';
import { AccountFacade } from '@mode/accounts/contract';
import { ModelRegistrationService } from '@mode/legacy/data-access-model-js';
import { AppState } from '@mode/legacy/types-app-state';
import { DEFAULT_CHART_FORMAT, LegacyRouterService, MetaTags, MetaTagsService } from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { Store } from '@ngrx/store';
import type { TransitionService, UrlService } from '@uirouter/core';
import { first } from 'rxjs/operators';
import { ClientActionType } from './actions/client.actions';
import { EDITOR_ACTIONS } from './actions/editor-actions';
import { ModeCommonConstantsModule } from './modules/mode.common/constants';

export function mark(measureName: string, markName: string) {
  if (typeof __markMeasure === 'function') {
    __markMeasure(measureName, markName);
  }
}

// The default as of immer@8 is to freeze by default. Previously, autofreeze
// by default was disabled in production envs. There's a decent launching
// point here for why this change was made: https://github.com/immerjs/immer/issues/687#issuecomment-728881754
// ---
// I'm leaving this here, redundantly, in case one decides we need to wrap
// this in an env check before enabling it: ie. to restore legacy behavior
setAutoFreeze(true);

// Reasonable !es2015 compliance test
if (
  // No global Proxy
  typeof self.Proxy !== 'function' ||
  // No global Symbol
  typeof self.Symbol !== 'function' ||
  // Or, Symbol doesn't return the right type
  typeof Symbol('x') !== 'symbol'
) {
  enableES5();
}

angular.module(ModeCommonConstantsModule).constant('EDITOR_ACTIONS', Object.freeze(EDITOR_ACTIONS));
angular.module(ModeCommonConstantsModule).constant('DEFAULT_CHART_FORMAT', Object.freeze(DEFAULT_CHART_FORMAT));

@Injectable()
export class LegacyBootstrapService {
  constructor(
    private router: Router,
    private observabilityService: ObservabilityService,
    private store: Store<AppState>,
    private legacyRouterService: LegacyRouterService,
    private meta: MetaTagsService,
    private accountFacade: AccountFacade,
    private ngZone: NgZone,
    // Need to Register Models on startup
    _: ModelRegistrationService
  ) {}

  public async bootstrap(angularJsAppLoader: () => Promise<void>, upgrade: UpgradeModule, injector: Injector) {
    this.loadCurrentUserAndOrg();
    this.enableNgZoneInAceEditor();

    const applicationElement = document.querySelector('[mode-app]');
    const moduleName = applicationElement.getAttribute('mode-app');

    const doBootstrap = () => {
      upgrade.bootstrap(applicationElement, [moduleName], { strictDi: true });
    };

    await angularJsAppLoader();
    doBootstrap();
    this.markBootstrapMetrics();

    const $urlService: UrlService = injector.get('$urlService');
    const $transitions: TransitionService = injector.get('$transitions');
    this.syncAngularAndAngularJsRouters($transitions, $urlService); // Initial ui-router sync
  }

  private syncAngularAndAngularJsRouters($transitions: TransitionService, $urlService: UrlService) {
    /**
     * Fire the angular router. This is necessary even on delegated routes to cleanup
     * router outlets and update the internal router states (keep back/forward buttons working).
     */
    $transitions.onSuccess({}, (transition) => {
      const toUrl = $urlService.url();
      if (this.router.url !== toUrl) {
        this.router.navigateByUrl(toUrl, { skipLocationChange: true });
      }
    });

    $transitions.onStart({}, (transition) => {
      // Initialize shared legacy router service state
      // Note: to use this state your legacy module's ui router config
      // must defer initialization until the $urlService.sync() call
      // in this bootstrap file using `deferIntercept()`
      // See lib/shared/util-js/README.md for more information
      this.legacyRouterService.loadRouteState({
        name: transition.to().name,
        url: $urlService.url(),
        params: transition.params(),
      });
    });

    /**
     * Sync the ui-router state when Angular router changes routes
     */
    this.router.events.subscribe((ev) => {
      if (ev instanceof NavigationStart) {
        /**
         * need to avoid popstate in one of the routers, because if both handle it
         * then ui-router can end up with ignored route transitions, which causes
         * problems.
         */
        if (ev.navigationTrigger === 'popstate') {
          this.router.navigateByUrl(this.router.url);
        } else {
          if ($urlService.url() !== ev.url.toString()) {
            $urlService.listen();
            $urlService.sync();
          }
        }
      }
    });

    $urlService.listen();
    $urlService.sync();
  }

  private enableNgZoneInAceEditor() {
    // need to make NgZone available to our custom ace editor plugins
    if (window?.ace) {
      window.ace.define('NgZone', [], () => this.ngZone);
    }
  }

  private loadCurrentUserAndOrg() {
    const username = this.meta.getTag(MetaTags.CurrentUsername);
    const selectedOrg = this.meta.getTag(MetaTags.OwnerUsername);

    this.accountFacade.loadCurrentUser(username);

    if (selectedOrg) {
      this.accountFacade.loadCurrentOrg(selectedOrg);
    }
  }

  private markBootstrapMetrics() {
    // record end times for page a span bootstrap metrics
    this.observabilityService.mark(ObservabilityEvents.LEGACY_LOAD, {
      startEvent: ObservabilityEvents.APP_INIT,
      measure: true,
    });
    this.observabilityService.mark(ObservabilityEvents.PAGE_BOOTSTRAP);
  }
}
